import { useState } from 'react';
import { Info, Clock, Target, X, Sparkles } from 'lucide-react';

export default function SaveTheCatView({ analysis, onBeatSelect, selectedBeat, onRewriteBeat }) {
  const [selectedBeatDetail, setSelectedBeatDetail] = useState(null);

  const totalPages = 110;

  const getBeatPosition = (beat) => {
    return (beat.startPage / totalPages) * 100;
  };

  const getBeatWidth = (beat) => {
    return Math.max(((beat.endPage - beat.startPage) / totalPages) * 100, 1);
  };

  const getBeatColor = (health) => {
    switch(health) {
      case 'good': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'poor': return 'bg-red-500';
      default: return 'bg-slate-400';
    }
  };

  const getHealthText = (health) => {
    switch(health) {
      case 'good': return 'Strong';
      case 'warning': return 'Moderate';
      case 'poor': return 'Weak';
      default: return 'Unknown';
    }
  };

  if (!analysis || !analysis.beats || analysis.beats.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <Info className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p>No Save the Cat analysis available</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Timeline Visualization */}
      <div className="bg-green-50 rounded-lg p-6 border border-green-200">
        <h3 className="text-lg font-semibold text-green-900 mb-6">Beat Timeline</h3>

        {/* Page markers */}
        <div className="relative mb-8">
          <div className="h-2 bg-slate-200 rounded-full relative">
            {[0, 25, 50, 75, 100].map(percent => (
              <div
                key={percent}
                className="absolute top-0 w-px h-6 bg-slate-400"
                style={{ left: `${percent}%` }}
              />
            ))}
          </div>

          {/* Beat positions */}
          <div className="relative h-12 mt-2">
            {analysis.beats.map((beat, index) => (
              <div
                key={index}
                className={`absolute h-8 rounded cursor-pointer transition-all hover:h-10 hover:opacity-90 ${getBeatColor(beat.health)}`}
                style={{
                  left: `${getBeatPosition(beat)}%`,
                  width: `${getBeatWidth(beat)}%`,
                  top: '0px'
                }}
                onClick={() => {
                  setSelectedBeatDetail(beat);
                  if (onBeatSelect) onBeatSelect(beat);
                }}
                title={`${beat.beat?.name || beat.name} (${beat.health})`}
              />
            ))}
          </div>

          {/* Page labels */}
          <div className="flex justify-between text-xs text-slate-500 mt-2">
            <span>Page 1</span>
            <span>25</span>
            <span>55</span>
            <span>85</span>
            <span>110</span>
          </div>
        </div>

        {/* Timeline Legend */}
        <div className="flex items-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span>Strong Beat</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <span>Moderate Beat</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span>Weak Beat</span>
          </div>
        </div>
      </div>

      {/* Beat Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {analysis.beats.map((beat, index) => {
          const beatName = beat.beat?.name || beat.name || `Beat ${index + 1}`;
          const beatDescription = beat.beat?.description || beat.notes || '';

          return (
            <div
              key={index}
              className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md bg-white ${
                selectedBeat?.name === beatName ? 'ring-2 ring-green-500 bg-green-50' : 'hover:border-green-300'
              }`}
              onClick={() => {
                setSelectedBeatDetail(beat);
                if (onBeatSelect) onBeatSelect(beat);
              }}
            >
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-semibold text-slate-900">{beatName}</h4>
                <span className="text-xs text-slate-500 flex items-center">
                  <Clock className="w-3 h-3 mr-1" />
                  p{beat.startPage}-{beat.endPage}
                </span>
              </div>

              <p className="text-sm text-slate-600 mb-3 line-clamp-2">{beatDescription}</p>

              <div className="flex items-center justify-between">
                <span className={`text-xs px-2 py-1 rounded font-medium ${
                  beat.health === 'good' ? 'bg-green-100 text-green-800' :
                  beat.health === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {getHealthText(beat.health)}
                </span>

                <div className="flex items-center text-slate-400">
                  <Info className="w-4 h-4" />
                </div>
              </div>

              {/* Rewrite Button */}
              {beat.health !== 'good' && onRewriteBeat && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRewriteBeat(beat, 'save-the-cat');
                  }}
                  className="mt-3 w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center justify-center space-x-2"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Improve This Beat</span>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Beat Detail Modal */}
      {selectedBeatDetail && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedBeatDetail(null)}
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-slate-900">
                  {selectedBeatDetail.beat?.name || selectedBeatDetail.name}
                </h3>
                <button
                  onClick={() => setSelectedBeatDetail(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-4 text-sm text-slate-600">
                  <span>Pages {selectedBeatDetail.startPage}-{selectedBeatDetail.endPage}</span>
                  <span>•</span>
                  <span>{getHealthText(selectedBeatDetail.health)} confidence</span>
                  <span>•</span>
                  <span>{selectedBeatDetail.endPage - selectedBeatDetail.startPage + 1} pages</span>
                </div>

                {selectedBeatDetail.beat?.description && (
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center">
                      <Target className="w-4 h-4 mr-2 text-green-600" />
                      Expected Function
                    </h4>
                    <p className="text-slate-700 bg-slate-50 p-3 rounded">{selectedBeatDetail.beat.description}</p>
                  </div>
                )}

                {selectedBeatDetail.notes && (
                  <div>
                    <h4 className="font-semibold mb-2">Analysis</h4>
                    <p className="text-slate-700">{selectedBeatDetail.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
